"use client";

import { useId, useRef, useState, type KeyboardEvent } from "react";
import { ReferenceShot } from "./reference-shot";

export type ReferenceTabItem = {
  /** Schlüssel aus REFERENCE_SITES (z. B. „siebenson“). */
  refKey: string;
  /** Reiter-Beschriftung, z. B. „Bau“. */
  label: string;
  /** Ein Satz aus `portfolio_projects.summary` oder null. */
  summary?: string | null;
  caseHref?: string | null;
};

/**
 * Referenz-Screenshots als Reiter nach dem WAI-ARIA-Vorgehen „Tabs“ (Auftrag
 * 2026-10-28, Paket 3: Hero der Website-Seite). Alle Panels sind im HTML,
 * nur das aktive ist sichtbar – das erste Bild lädt mit Priorität (LCP),
 * die anderen erst beim Wechsel. Pfeiltasten, Pos1 und Ende wechseln den
 * Reiter; der Fokus bleibt in der Reiterleiste (roving tabindex).
 */
export function ReferenceTabs({ items, label = "Referenzen nach Branche" }: { items: ReferenceTabItem[]; label?: string }) {
  const [active, setActive] = useState(0);
  const base = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const focusTab = (i: number) => {
    const next = (i + items.length) % items.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        focusTab(i + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        focusTab(i - 1);
        break;
      case "Home":
        e.preventDefault();
        focusTab(0);
        break;
      case "End":
        e.preventDefault();
        focusTab(items.length - 1);
        break;
    }
  };

  if (items.length === 0) return null;

  return (
    <div>
      <div role="tablist" aria-label={label} className="mb-4 flex flex-wrap gap-2">
        {items.map((it, i) => {
          const selected = i === active;
          return (
            <button
              key={it.refKey}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${i}`}
              aria-selected={selected}
              aria-controls={`${base}-panel-${i}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(i)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className="rounded-full border px-4 py-2 text-sm font-semibold transition-colors"
              style={
                selected
                  ? { borderColor: "var(--color-brand-700)", backgroundColor: "var(--color-brand-700)", color: "#FFFFFF" }
                  : { borderColor: "var(--border)", backgroundColor: "var(--card-bg)", color: "var(--text-secondary)" }
              }
            >
              {it.label}
            </button>
          );
        })}
      </div>
      {items.map((it, i) => (
        <div
          key={it.refKey}
          role="tabpanel"
          id={`${base}-panel-${i}`}
          aria-labelledby={`${base}-tab-${i}`}
          hidden={i !== active}
          tabIndex={0}
        >
          <ReferenceShot refKey={it.refKey} summary={it.summary} caseHref={it.caseHref} priority={i === 0} />
        </div>
      ))}
    </div>
  );
}
